/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from "react";
import _ from "lodash";
import { Form, InputNumber, Modal, Row, Col, Spin } from "antd";
import {
  SECOND_PART_OF_FORM,
  DROP_DOWN_VALIDATION_MESSAGE,
  FIELDS_LABEL,
  ADD_OPTION_INITIAL_STATE,
  INPUT_KEYS,
  MODAL_BUTTON_KEYS,
  RADIO_GROUP_BUTTONS,
  BUTTON_NAMES,
  IS_BUY,
  IS_CALL,
} from "constants/addOption";
import { FormItem, RadioGroupButtons } from "./AddOptionHelperComponents";
// Custom Hooks
import usePricerFetchFunctions from "hooks/usePricerFetchFunctions";
import useDraggableModal from "hooks/useDraggableModal";
// Helpers
import { findGCD, reformatSubmitData } from "helpers/Pricer/helperFunc";
// API
import { submitAddOptionAPI } from "api/pricerApi";
// Context
import { PricerContext } from "contexts/PricerContext";
// Style
import "./AddOptionModal.scss";
import { GlobalContext } from "contexts/GlobalContext";
import Tenors from "./Tenors";
import Strikes from "./Strikes";
import Ratio from "./Ratio";
import LiveOption from "./LiveOption";
import UnderlyingOption from "./UnderlyingOption";
import SelectDropdown from "./SelectDropdown";
import {
  ACTIONS,
  CATEGORIES,
  COMPONENT_NAMES,
  CONTEXTS,
  DESCRIPTIONS,
  ELEMENTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";
import {
  CancelButton,
  PrimaryButton,
  SecondaryButton,
} from "components/Common/CustomButtons/CustomButtons";
import AddTenorTablePopup from "./AddTenorTablePopup";
import ShortcutButtons from "./ShortcutButtons";

const { Item } = Form;

const CreateOptionModal = (props) => {
  const {
    refetchOptionRow,
    setIsAddOptionSubmitted,
    setIsCellEditing,
    setRowEditingData,
  } = useContext(PricerContext);
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  //#States
  const [addOptionData, setAddOptionData] = useState({
    ...ADD_OPTION_INITIAL_STATE,
    ...SECOND_PART_OF_FORM,
  });
  const [tenorDataSelectedButtons, setTenorDataSelectedButtons] = useState({});
  const [strikeValues, setStrikeValues] = useState({});
  const [ratioValues, setRatioValues] = useState([]);
  const [isCalcRatioVisible, setIsCalcRatioVisible] = useState({
    [RADIO_GROUP_BUTTONS.LCM_BUTTONS]: RADIO_GROUP_BUTTONS["1"],
  });
  const [calcFieldsValues, setCalcFieldsValues] = useState({});
  const [underlyingData, setUnderlyingData] = useState({});
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isLiveChecked, setIsLiveChecked] = useState(false);
  const [isBespokeChecked, setIsBespokeChecked] = useState({});
  const [bespokePeriodId, setBespokePeriodId] = useState(1);
  const [isAddTenorPopupOpen, setIsAddTenorPopupOpen] = useState(false);
  //Hooks
  const fetchedData = usePricerFetchFunctions(addOptionData, bespokePeriodId);
  const { modalRender, title: draggableTitle } = useDraggableModal(
    props.isAddOptionModalVisible,
    "Add Option"
  );

  //Constants
  // const CONTRACT_STYLE = addOptionData[FIELDS_LABEL.CONTRACT_STYLE.key];
  const TYPE_OPTION = addOptionData[FIELDS_LABEL.TYPE.key];
  const COST_OF_CARRY = addOptionData[FIELDS_LABEL.COST_OF_CARRY.key];
  const AVERAGES = addOptionData[FIELDS_LABEL.AVERAGES.key];
  const CONSTRAINTS = fetchedData[FIELDS_LABEL.CONSTRAINTS.key];
  const UNDERLYING_COUNT = CONSTRAINTS?.[FIELDS_LABEL.UNDERLYING_COUNT.key];
  const UNDERLYING = addOptionData[FIELDS_LABEL.UNDERLYING.key];
  const FETCHED_CONTRACT_STYLE = fetchedData[FIELDS_LABEL.COMMODITY.key];
  const COMMODITY = addOptionData[FIELDS_LABEL.COMMODITY.key];

  // Check if the selected commodity/shortcut contains "cso"
  const isCSO = useMemo(() => {
    if (!COMMODITY?.label) return false;
    return COMMODITY.label.toLowerCase().includes("cso");
  }, [COMMODITY]);

  //When Legs Change It Should Fetch Types
  const onLegsChange = async (key, value) => {
    if (key === FIELDS_LABEL.LEGS.key) {
      setAddOptionData({
        ...addOptionData,
        [FIELDS_LABEL.LEGS.key]: value,
        [FIELDS_LABEL.TYPE.key]: "",
      });
      return;
    }

    setAddOptionData({ ...addOptionData, [key]: value });
  };

  const onSelectionChange = (key, val) => {
    if (key === FIELDS_LABEL.TYPE.key) {
      setAddOptionData({
        ...addOptionData,
        ...SECOND_PART_OF_FORM,
        [key]: val,
      });
      setStrikeValues({});
      setRatioValues({});
      setUnderlyingData({});
      return;
    }
    if (key === FIELDS_LABEL.LEGS.key) {
      setAddOptionData({ ...ADD_OPTION_INITIAL_STATE, ...SECOND_PART_OF_FORM });
      setStrikeValues({});
      setRatioValues({});
      setUnderlyingData({});
      return;
    }

    if (key === FIELDS_LABEL.COMMODITY.key) {
      setAddOptionData({
        ...addOptionData,
        [FIELDS_LABEL.CONTRACT_STYLE.key]: "",
        [key]: val,
      });
      return;
    }

    setAddOptionData({ ...addOptionData, [key]: val });
  };

  const onCalcChange = useCallback(
    (value, key) => {
      setIsCalcRatioVisible({ ...isCalcRatioVisible, [key]: value });
    },
    [isCalcRatioVisible]
  );

  const showCostOfCarryAndAverage = useMemo(() => {
    if (
      !addOptionData?.[FIELDS_LABEL.COMMODITY.key] &&
      !addOptionData?.[FIELDS_LABEL.TYPE.key]
    )
      return;
    const typeSelected = COMMODITY?.label?.toLowerCase();
    if (!typeSelected?.includes("apo") || !TYPE_OPTION) return;

    return (
      <>
        <FormItem label={FIELDS_LABEL.COST_OF_CARRY.label}>
          <InputNumber
            onPressEnter={(event) => event.target.blur()}
            className="cost-of-carry"
            style={{ width: "130px" }}
            min={0}
            max={100}
            addonAfter={"%"}
            value={COST_OF_CARRY}
            onChange={(value) =>
              onLegsChange(FIELDS_LABEL.COST_OF_CARRY.key, value)
            }
          />
        </FormItem>
        <FormItem label={FIELDS_LABEL.AVERAGES.label}>
          <InputNumber
            onPressEnter={(event) => event.target.blur()}
            className="input-number average"
            min={0}
            value={AVERAGES}
            onChange={(value) => onLegsChange(FIELDS_LABEL.AVERAGES.key, value)}
          />
        </FormItem>
      </>
    );
  }, [addOptionData]);

  const onSubmitModal = async () => {
    setIsButtonLoading(true);
    const prepareBody = {
      strikeValues,
      ratioValues,
      ...addOptionData,
      checkedBespoke: isBespokeChecked,
      underlyingData,
      [FIELDS_LABEL.TENOR.key]: tenorDataSelectedButtons,
      bespokePeriodId,
    };
    const res = reformatSubmitData(prepareBody);
    sendEvent({
      category: CATEGORIES.PRICER,
      description: DESCRIPTIONS.ADD_OPTION,
      context: CONTEXTS.ADD_OPTION,
      action: ACTIONS.CLICK,
      element: ELEMENTS.SUBMIT,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_ADD_OPTION_SUBMIT,
    });
    submitAddOptionAPI(res)
      .then((res) => {
        // Reset cell editing state before refetching to prevent grid freeze
        setIsCellEditing(false);
        setRowEditingData(null);
        refetchOptionRow();
        props.setIsAddOptionModalVisible(false);
        setTimeout(() => setIsAddOptionSubmitted(true), 1000);
        setIsButtonLoading(false);
      })
      .catch(() => {
        // Reset cell editing state on error as well
        setIsCellEditing(false);
        setRowEditingData(null);
        setIsButtonLoading(false);
      });
  };

  const onCancel = useCallback(() => {
    props.setIsAddOptionModalVisible(false);
  }, []);

  const isSubmitDisabled = () => {
    let tenorCheckForDisable = false;
    const tenorGroupsCount = CONSTRAINTS?.[FIELDS_LABEL.TENOR.key] || 0;

    if (tenorGroupsCount > 0) {
      for (let i = 0; i < tenorGroupsCount; i++) {
        const isBespoke = isBespokeChecked[i];
        const tenorData = tenorDataSelectedButtons[i];

        if (isCSO || isBespoke) {
          if (
            !Array.isArray(tenorData) ||
            tenorData.length === 0 ||
            (isCSO && tenorData.length > 2)
          ) {
            tenorCheckForDisable = true;
            break;
          }
        } else {
          if (!tenorData || Array.isArray(tenorData)) {
            tenorCheckForDisable = true;
            break;
          }
        }
      }
    }

    const hasAllTenorSelections =
      tenorGroupsCount > 0 &&
      Array.from({ length: tenorGroupsCount }).every((_, index) => {
        const tenorData = tenorDataSelectedButtons[index];
        if (isCSO || isBespokeChecked[index]) {
          return (
            Array.isArray(tenorData) &&
            tenorData.length > 0 &&
            (!isCSO || tenorData.length <= 2)
          );
        }
        return tenorData !== undefined && !Array.isArray(tenorData);
      });

    if (
      _.isEmpty(strikeValues) ||
      !CONSTRAINTS ||
      tenorGroupsCount === 0 ||
      !hasAllTenorSelections ||
      tenorCheckForDisable
    )
      return true;

    //Strikes And Ratios Validations
    const strikesAPINumber = CONSTRAINTS[FIELDS_LABEL.STRIKE.key]?.length;
    const ratioAPINumber = CONSTRAINTS[FIELDS_LABEL.RATIO.key]?.length;
    const filledStrikes =
      (strikeValues &&
        Object.values(strikeValues).filter(
          (item) =>
            item[INPUT_KEYS.VALUE] !== null && item[INPUT_KEYS.BUTTON] !== null
        )?.length) ||
      0;
    const filledRatio =
      (ratioValues &&
        Object.values(ratioValues).filter(
          (item) =>
            item[INPUT_KEYS.VALUE] !== null && item[INPUT_KEYS.BUTTON] !== null
        )?.length) ||
      0;
    const isUnderlyingFilled =
      UNDERLYING_COUNT === Object.values(underlyingData).filter(Boolean).length;
    let isAvgAndCostOfCarryFilled;

    if (COMMODITY?.label?.includes("APO")) {
      isAvgAndCostOfCarryFilled = !(
        COST_OF_CARRY !== null && AVERAGES !== null
      );
    } else {
      isAvgAndCostOfCarryFilled = false;
    }
    //End Of Strikes And Ratios Validations
    return (
      filledStrikes !== strikesAPINumber ||
      filledRatio !== ratioAPINumber ||
      !isUnderlyingFilled ||
      isAvgAndCostOfCarryFilled
    );
  };

  const showCalc = () => {
    const showInputNumbers =
      isCalcRatioVisible[RADIO_GROUP_BUTTONS.Q_BUTTON] ===
      RADIO_GROUP_BUTTONS.Q;

    return (
      <Item>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Row gutter={[24, 24]}>
              <Col sm={12} md={8}>
                <RadioGroupButtons
                  defaultValue={RADIO_GROUP_BUTTONS.R}
                  firstButton={RADIO_GROUP_BUTTONS.R}
                  lastButton={RADIO_GROUP_BUTTONS.Q}
                  onChange={(value) =>
                    onCalcChange(value, RADIO_GROUP_BUTTONS.Q_BUTTON)
                  }
                />
              </Col>
              <Col sm={12} md={8}>
                {showInputNumbers && (
                  <RadioGroupButtons
                    firstButton={RADIO_GROUP_BUTTONS["1"]}
                    defaultValue={RADIO_GROUP_BUTTONS["1"]}
                    lastButton={RADIO_GROUP_BUTTONS.LCM}
                    onChange={(value) =>
                      onCalcChange(value, RADIO_GROUP_BUTTONS.LCM_BUTTONS)
                    }
                  />
                )}
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <div className="horizontal-scroll__container">
              {showInputNumbers &&
                CONSTRAINTS?.[FIELDS_LABEL.STRIKE.key].map((item, index) => (
                  <InputNumber
                    onPressEnter={(event) => event.target.blur()}
                    className="ratio-and-strike__input-number"
                    min={isCSO ? undefined : 1}
                    value={props.value}
                    disabled={props.isDisabled}
                    onChange={(value) => {
                      setCalcFieldsValues({
                        ...calcFieldsValues,
                        [index]: value,
                      });
                    }}
                  />
                ))}
            </div>
          </Col>
        </Row>
      </Item>
    );
  };

  useEffect(() => {
    setIsBespokeChecked({});
    setTenorDataSelectedButtons({});
  }, [
    addOptionData[FIELDS_LABEL.LEGS.key],
    COMMODITY,
    TYPE_OPTION,
    isCSO, // Reset when CSO status changes
  ]);

  //! Calculate Values For Ratio Fields
  useEffect(() => {
    if (
      CONSTRAINTS?.[FIELDS_LABEL.STRIKE.key]?.length !==
      Object.keys(calcFieldsValues).length
    )
      return;
    const isCalcFieldContainNull = Object.values(calcFieldsValues).find(
      (item) => item === null
    );
    if (isCalcFieldContainNull) return;

    const minNumFilled = _.isEmpty(calcFieldsValues)
      ? 1
      : Math.min(...Object.values(calcFieldsValues));

    if (
      isCalcRatioVisible[RADIO_GROUP_BUTTONS.LCM_BUTTONS] ===
      RADIO_GROUP_BUTTONS["1"]
    ) {
      const obj = {};
      Object.values(ratioValues).forEach(
        (item, index) =>
        (obj[index] = {
          value: calcFieldsValues[index] / minNumFilled,
          [INPUT_KEYS.BUTTON]: ratioValues[index][INPUT_KEYS.BUTTON],
        })
      );
      setRatioValues({ ...ratioValues, ...obj });
    }

    if (
      isCalcRatioVisible[RADIO_GROUP_BUTTONS.LCM_BUTTONS] ===
      RADIO_GROUP_BUTTONS.LCM
    ) {
      const calcValues = Object.values(calcFieldsValues);
      const greatestCommonDivisor = findGCD(calcValues);

      const obj = {};
      Object.values(ratioValues).forEach(
        (item, index) =>
        (obj[index] = {
          value: calcFieldsValues[index] / greatestCommonDivisor,
          [INPUT_KEYS.BUTTON]: ratioValues[index][INPUT_KEYS.BUTTON],
        })
      );
      setRatioValues({ ...ratioValues, ...obj });
    }
  }, [
    calcFieldsValues,
    isCalcRatioVisible[RADIO_GROUP_BUTTONS.LCM_BUTTONS],
    isCalcRatioVisible,
  ]);

  //! Effect To Fill Ratio State If It's Already Filled From API
  useEffect(() => {
    const obj = {};
    CONSTRAINTS?.[FIELDS_LABEL.RATIO.key].forEach((item, index) => {
      if (item.value !== null) {
        obj[index] = {
          [INPUT_KEYS.VALUE]: item[INPUT_KEYS.VALUE],
          [INPUT_KEYS.BUTTON]: item[IS_BUY]
            ? BUTTON_NAMES.BUY
            : BUTTON_NAMES.SELL,
          [IS_BUY]: item[IS_BUY],
        };
      }
    });

    if (!Object.values(obj)?.length) return;

    setRatioValues({ ...ratioValues, ...obj });
  }, [CONSTRAINTS?.[FIELDS_LABEL.RATIO.key]]);

  //! Effect To Fill Live State If It's Already Filled From API
  useEffect(() => {
    const isChecked = CONSTRAINTS?.live;
    setIsLiveChecked(() => isChecked);
    onLegsChange(FIELDS_LABEL.LIVE.key, isChecked ? 1 : 0);
  }, [CONSTRAINTS?.live]);

  //! Effect To Fill Strike State If It's Already Filled From API
  useEffect(() => {
    const obj = {};
    CONSTRAINTS?.[FIELDS_LABEL.STRIKE.key].forEach((item, index) => {
      if (item.value !== null) {
        obj[index] = {
          [INPUT_KEYS.VALUE]: item[INPUT_KEYS.VALUE],
          [INPUT_KEYS.BUTTON]: item[IS_CALL]
            ? BUTTON_NAMES.CALL
            : BUTTON_NAMES.PUT,
          [IS_CALL]: item[IS_CALL],
        };
      }
    });

    if (!Object.values(obj)?.length) return;

    setStrikeValues({ ...strikeValues, ...obj });
  }, [CONSTRAINTS?.[FIELDS_LABEL.STRIKE.key]]);

  const shortcutItems = fetchedData[FIELDS_LABEL.COMMODITY.key]?.items;

  //! Effect To Fill Shortcut If The API Returns Only One Style
  useEffect(() => {
    if (shortcutItems?.length !== 1) return;

    onSelectionChange(FIELDS_LABEL.COMMODITY.key, shortcutItems[0]);
  }, [shortcutItems]);

  useEffect(() => {
    const isRatioDataExists = CONSTRAINTS?.[FIELDS_LABEL.RATIO.key];
    if (!isRatioDataExists) return;

    const formattedData = isRatioDataExists.map((item) => ({
      button: item["is_buy"] ? BUTTON_NAMES.BUY : BUTTON_NAMES.SELL,
      is_buy: item["is_buy"],
      value: item["value"],
    }));

    setRatioValues(formattedData);
  }, [CONSTRAINTS?.[FIELDS_LABEL.RATIO.key]]);

  //? Reset Add Option State
  useEffect(() => {
    setIsAddOptionSubmitted(false);
  }, []);

  useEffect(() => {
    if (!props.isAddOptionModalVisible) {
      setIsAddTenorPopupOpen(false);
    }
  }, [props.isAddOptionModalVisible]);

  return (
    <>
      <Modal
        className={`add-option-form__container ${dark_Theme && "ant-modal-dark"}`}
        open={props.isAddOptionModalVisible}
        onOk={onSubmitModal}
        onCancel={() => props.setIsAddOptionModalVisible(false)}
        modalRender={modalRender}
        title={draggableTitle}
        footer={[
          <CancelButton key="back" onClick={onCancel}>
            {MODAL_BUTTON_KEYS.CANCEL}
          </CancelButton>,
          <SecondaryButton
            key="add-tenor"
            onClick={() => setIsAddTenorPopupOpen(true)}
          >
            {MODAL_BUTTON_KEYS.ADD_TENOR}
          </SecondaryButton>,
          <PrimaryButton
            key="submit"
            loading={isButtonLoading}
            onClick={onSubmitModal}
            disabled={isSubmitDisabled()}
          >
            {MODAL_BUTTON_KEYS.SUBMIT}
          </PrimaryButton>,
        ]}
        width={"90vw"}
        getContainer={"#content-root"}
      >
        <Form layout="horizontal">
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <FormItem label={FIELDS_LABEL.LEGS.label}>
                <InputNumber
                  onPressEnter={(event) => event.target.blur()}
                  min={1}
                  onChange={(value) => onLegsChange(FIELDS_LABEL.LEGS.key, value)}
                />
              </FormItem>
            </Col>
            {/*Commodity DropDown*/}
            <Col span={24}>
              <FormItem
                className="add-option-form__commodity"
                label={FIELDS_LABEL.COMMODITY.label}
              >
                {fetchedData.commodityLoading ? (
                  <Spin size="medium" className="mx-4" />
                ) : (
                  <ShortcutButtons
                    shortcuts={fetchedData[FIELDS_LABEL.COMMODITY.key]}
                    selectedValue={COMMODITY?.value}
                    onSelect={(item) =>
                      onSelectionChange(FIELDS_LABEL.COMMODITY.key, item)
                    }
                  />
                )}
              </FormItem>
            </Col>

            {/* <SelectDropdown
            options={fetchedData[FIELDS_LABEL.COMMODITY.key]}
            placeholder={FIELDS_LABEL.COMMODITY.PLACEHOLDER}
            onChange={(val) => {
              onSelectionChange(FIELDS_LABEL.COMMODITY.key, val);
            }}
            value={!COMMODITY ? undefined : COMMODITY}
            label={FIELDS_LABEL.COMMODITY.label}
            isLoading={fetchedData.commodityLoading}
          /> */}
            {/* End Of Commodity DropDown */}

            {/* Contract Style DropDown */}
            {/* <SelectDropdown
            isLoading={fetchedData.contractStyleLoading}
            options={FETCHED_CONTRACT_STYLE}
            placeholder={FIELDS_LABEL.CONTRACT_STYLE.PLACEHOLDER}
            onChange={(val) => {
              onSelectionChange(FIELDS_LABEL.CONTRACT_STYLE.key, val);
            }}
            value={!CONTRACT_STYLE ? undefined : CONTRACT_STYLE}
            label={FIELDS_LABEL.CONTRACT_STYLE.label}
          /> */}
            {/* End Of Contract Style DropDown */}

            {/* Type DropDown */}
            <SelectDropdown
              isLoading={fetchedData.typeLoading}
              options={fetchedData[FIELDS_LABEL.TYPE.key]}
              placeholder={FIELDS_LABEL.TYPE.PLACEHOLDER}
              onChange={(val) => {
                onSelectionChange(FIELDS_LABEL.TYPE.key, val);
              }}
              value={!TYPE_OPTION ? undefined : TYPE_OPTION}
              notFoundContent={
                !fetchedData[FIELDS_LABEL.LEGS.key] && (
                  <span className="required-validation">
                    {DROP_DOWN_VALIDATION_MESSAGE}
                  </span>
                )
              }
              label={FIELDS_LABEL.TYPE.label}
            />

            {/* End Of Type DropDown */}

            {/*Show Cost Of Carry And Average*/}
            <Col span={24}>{showCostOfCarryAndAverage}</Col>
            {/*End Of Show Cost Of Carry And Average*/}
            {COMMODITY && TYPE_OPTION && CONSTRAINTS && (
              <UnderlyingOption
                UNDERLYING={UNDERLYING}
                UNDERLYING_COUNT={UNDERLYING_COUNT}
                setUnderlyingData={setUnderlyingData}
                underlyingData={underlyingData}
              />
            )}

            {/*Tenor And Live*/}
            {COMMODITY && TYPE_OPTION && CONSTRAINTS && (
              <>
                <Tenors
                  fetchedData={fetchedData}
                  isBespokeChecked={isBespokeChecked}
                  setIsBespokeChecked={setIsBespokeChecked}
                  setTenorDataSelectedButtons={setTenorDataSelectedButtons}
                  tenorDataSelectedButtons={tenorDataSelectedButtons}
                  isCSO={isCSO}
                  bespokePeriodId={bespokePeriodId}
                  setBespokePeriodId={(id) => {
                    setBespokePeriodId(id);
                    setTenorDataSelectedButtons((prev) => {
                      const cleared = { ...prev };
                      Object.keys(isBespokeChecked).forEach((k) => {
                        if (isBespokeChecked[k]) cleared[k] = [];
                      });
                      return cleared;
                    });
                  }}
                  bespokePeriods={fetchedData.bespokePeriods}
                  bespokeTenorsLoading={fetchedData.bespokeTenorsLoading}
                />

                <LiveOption
                  fetchedData={fetchedData}
                  onLegsChange={onLegsChange}
                  isLiveChecked={isLiveChecked}
                  setIsLiveChecked={setIsLiveChecked}
                />
              </>
            )}

            {/*Show Ratio And Strikes*/}
            {TYPE_OPTION && CONSTRAINTS && (
              <>
                <Strikes
                  fetchedData={fetchedData}
                  setStrikeValues={setStrikeValues}
                  strikeValues={strikeValues}
                  isCSO={isCSO}
                />

                <Ratio
                  fetchedData={fetchedData}
                  isCalcRatioVisible={isCalcRatioVisible}
                  ratioValues={ratioValues}
                  setRatioValues={setRatioValues}
                />

                {/* Show Calc Section  */}
                <Col span={24}>{CONSTRAINTS["show_calc"] && showCalc()}</Col>
                {/* End Of Show Calc Section  */}
              </>
            )}
          </Row>
        </Form>
      </Modal>
      <AddTenorTablePopup
        open={isAddTenorPopupOpen}
        onClose={() => setIsAddTenorPopupOpen(false)}
      />
    </>
  );
};

export default CreateOptionModal;
